import { S3Client } from "@aws-sdk/client-s3"
import { Upload } from "@aws-sdk/lib-storage"
import { useCallback, useMemo, useRef, useState } from "react"
import { LuFileText, LuLoaderCircle, LuTrash2 } from "react-icons/lu"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { toast } from "sonner"
import * as tus from "tus-js-client"

import {
  addSrt,
  createCloudflareVideo,
  requestCloudflareCredentials,
  requestSignedUrl,
  requestSrtUpload,
  uploadSrt,
} from "~/v2/api/uploads"
import { deleteSrt, getVideo, getVideoSrt, updateLesson } from "~/v2/api/vaas"
import Button from "~/v2/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/v2/components/ui/card"
import Dropzone from "~/v2/components/ui/dropzone"
import VaasConfirmDialog from "~/v2/components/vaas/vaas-confirm-dialog"
import VaasSrtLanguageDialog from "~/v2/components/vaas/vaas-srt-language-dialog"
import VaasVideoPlayer from "~/v2/components/vaas/vaas-video-player"
import { SRT_LANGUAGES } from "~/v2/constants/languages"
import useThumbnail from "~/v2/hooks/use-thumbnail"
import type { VaasCaption, VaasId, VaasLesson } from "~/v2/types/vaas"
import getMediaFileName from "~/v2/utils/get-media-file-name"
import { openV2ErrorToast } from "~/v2/utils/toast"

type ApiData = Record<string, unknown>

type VaasMediaSectionProps = {
  isPublished?: boolean
  lesson: VaasLesson
}

type UpdateLessonMutationParams = {
  data: ApiData
  lessonId: VaasId
}

type SrtUploadRequest = {
  data: {
    fields: Record<string, string | Blob>
    url: string
  }
}

type S3SignedUrlResponse = {
  key: string
  secret: string
  session: string
}

type UploadRejection = {
  errors?: Array<{ code?: string }>
}

const VaasMediaSection = ({
  isPublished = false,
  lesson,
}: VaasMediaSectionProps) => {
  const queryClient = useQueryClient()
  const requestS3SignedUrlMutation = useMutation(requestSignedUrl)
  const createCloudflareVideoMutation = useMutation(createCloudflareVideo)
  const updateLessonMutation = useMutation(
    ({ data, lessonId }: UpdateLessonMutationParams) =>
      updateLesson(data, lessonId),
  )
  const requestSrtUploadMutation = useMutation(requestSrtUpload)
  const deleteSrtMutation = useMutation(deleteSrt)

  const [uploadProgress, setUploadProgress] = useState(0)
  const [showVideoLoading, setShowVideoLoading] = useState(false)
  const [videoProgressMessage, setVideoProgressMessage] = useState("")
  const [srtLoading, setSrtLoading] = useState(false)
  const [deleteCaptionId, setDeleteCaptionId] = useState<VaasId | null>(null)
  const [pendingSrtCaption, setPendingSrtCaption] =
    useState<VaasCaption | null>(null)
  const [pendingSrtFiles, setPendingSrtFiles] = useState<File[]>([])
  const [srtLanguageDialogOpen, setSrtLanguageDialogOpen] = useState(false)
  const cloudflareVideoIdRef = useRef<string | null>(null)
  const videoKeyRef = useRef<string | null>(null)

  const lessonThumbnail = useThumbnail(lesson?.thumbnail, 1920)

  const videoQuery = useQuery(
    ["vaas-video-url", lesson?.video_id],
    () => getVideo(lesson.video_id!),
    {
      enabled: Boolean(lesson?.video_id),
      staleTime: 1000 * 60 * 60,
    },
  )
  const mediaKeyName = getMediaFileName(videoQuery?.data?.data?.key)

  const setLessonCache = useCallback(
    (
      updater:
        | Partial<VaasLesson>
        | ((oldData: VaasLesson) => Partial<VaasLesson>),
    ) => {
      const apply = (oldData: VaasLesson | undefined) => {
        if (!oldData) {
          return undefined
        }
        const nextData =
          typeof updater === "function" ? updater(oldData) : updater
        return { ...oldData, ...nextData }
      }

      queryClient.setQueryData<VaasLesson | undefined>(
        ["vaas-lesson", lesson.id],
        apply,
      )
      queryClient.setQueryData<VaasLesson | undefined>(
        ["lesson", lesson.id],
        apply,
      )
      queryClient.setQueryData("lessons", (oldData: VaasLesson[] = []) =>
        oldData.map((item) =>
          item.id === lesson.id ? apply(item) || item : item,
        ),
      )
    },
    [lesson.id, queryClient],
  )

  const uploadVideoToS3 = useCallback(
    async (file: File, key: string) => {
      const data =
        (await requestS3SignedUrlMutation.mutateAsync()) as S3SignedUrlResponse
      const upload = new Upload({
        client: new S3Client({
          region: "us-east-1",
          credentials: {
            accessKeyId: data.key,
            secretAccessKey: data.secret,
            sessionToken: data.session,
          },
        }),
        params: {
          Body: file,
          Bucket: import.meta.env.VITE_UPLOAD_BUCKET,
          ContentType: file.type,
          Key: key,
        },
      })

      await upload.done()
    },
    [requestS3SignedUrlMutation],
  )

  const onVideoDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      const timestamp = +new Date()
      const key = `lessonVideos/${timestamp}-${file.name}`

      setUploadProgress(0)
      setShowVideoLoading(true)
      setVideoProgressMessage("Uploading video")
      videoKeyRef.current = key

      try {
        uploadVideoToS3(file, key)
          .then(() => {
            setVideoProgressMessage(
              "Video uploaded to S3, processing with Cloudflare",
            )
          })
          .catch(() => {})

        const { uploadURL } = await requestCloudflareCredentials({
          uploadLength: file.size,
          metadata: {
            filename: file.name,
            name: key,
          },
          name: key,
        })

        await new Promise<void>((resolve, reject) => {
          const upload = new tus.Upload(file, {
            chunkSize: 50 * 1024 * 1024,
            endpoint: uploadURL,
            onError: reject,
            onProgress: (bytesUploaded, bytesTotal) => {
              const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2)
              setUploadProgress(Number(percentage))
              if (Number(percentage) === 100) {
                setVideoProgressMessage("Video uploaded to Cloudflare")
              }
            },
            onSuccess: () => {
              const urlParts = upload.url?.split("/") || []
              const mediaIdWithParams = urlParts[urlParts.length - 1]
              cloudflareVideoIdRef.current =
                mediaIdWithParams?.split("?")[0] || null
              resolve()
            },
            retryDelays: [0, 3000, 5000, 10000, 20000],
            uploadSize: file.size,
          })
          upload.start()
        })

        const createdVideo = await createCloudflareVideoMutation.mutateAsync({
          cloudflare_video_id: cloudflareVideoIdRef.current,
          key,
          video_id: lesson.video_id || undefined,
        })

        await updateLessonMutation.mutateAsync({
          data: {
            video: {
              cloudflare_video_id: cloudflareVideoIdRef.current,
            },
            video_id: createdVideo.data.id,
          },
          lessonId: lesson.id,
        })

        setLessonCache((_oldData) => ({
          captions: [],
          duration: null,
          video: createdVideo.data,
          video_id: createdVideo.data.id,
        }))
        await queryClient.invalidateQueries(["vaas-lesson", lesson.id])
        await queryClient.invalidateQueries(["lesson", lesson.id])
        await queryClient.invalidateQueries(["vaas-video-url", lesson.video_id])
        toast.success("Video uploaded")
      } catch (err) {
        openV2ErrorToast(err, "There was an error uploading this video")
      } finally {
        setShowVideoLoading(false)
      }
    },
    [
      createCloudflareVideoMutation,
      lesson.id,
      lesson.video_id,
      queryClient,
      setLessonCache,
      updateLessonMutation,
      uploadVideoToS3,
    ],
  )

  const onVideoError = useCallback((rejectedFiles: UploadRejection[]) => {
    toast.error(
      rejectedFiles[0]?.errors?.[0]?.code === "file-invalid-type"
        ? "You can only upload a video file here"
        : "There was an error uploading that file",
    )
  }, [])

  const onSrtError = useCallback((rejectedFiles: UploadRejection[]) => {
    toast.error(
      rejectedFiles[0]?.errors?.[0]?.code === "file-invalid-type"
        ? "You can only upload an .srt file here"
        : "There was an error uploading that file",
    )
  }, [])

  const uploadSrtFile = async (
    file: File,
    captionId: VaasId | null,
    srtLanguage: string,
  ) => {
    if (!lesson.video_id) {
      toast.error("You must add a video before adding captions")
      return
    }

    setSrtLoading(true)
    try {
      const timestamp = +new Date()
      const request = (await requestSrtUploadMutation.mutateAsync({
        key: `srt/${srtLanguage}/${timestamp}-${file.name}`,
        type: "text/plain",
      })) as SrtUploadRequest
      await uploadSrt({ ...request.data, file })
      const addSrtResponse = await addSrt(
        {
          caption_id: captionId,
          key: request.data.fields.key,
          srt_language: srtLanguage,
        },
        lesson.video_id,
      )

      setLessonCache({
        captions: addSrtResponse.data,
      })
      await queryClient.invalidateQueries(["vaas-lesson", lesson.id])
      await queryClient.invalidateQueries(["lesson", lesson.id])
      toast.success("Caption updated")
    } catch (err) {
      openV2ErrorToast(err, "There was an error uploading this srt file")
    } finally {
      setSrtLoading(false)
    }
  }

  const onSrtDrop = async (
    acceptedFiles: File[],
    _videoUploaded?: boolean,
    caption: VaasCaption | null = null,
  ) => {
    setPendingSrtCaption(caption)
    setPendingSrtFiles(acceptedFiles)
    setSrtLanguageDialogOpen(true)
  }

  const handleOpenSrt = async (captionId: VaasId) => {
    try {
      const url = await getVideoSrt(captionId)
      window.open(url)
    } catch (err) {
      openV2ErrorToast(err, "There was an error showing the srt file")
    }
  }

  const handleDeleteSrt = async (captionId: VaasId | null) => {
    if (!captionId) {
      return
    }

    if (isPublished) {
      toast.error("Published VAAS media cannot be changed")
      return
    }

    try {
      await deleteSrtMutation.mutateAsync(captionId)
      setLessonCache((oldData) => ({
        captions: (oldData.captions || []).filter(
          (item) => item.id !== captionId,
        ),
      }))
      await queryClient.invalidateQueries(["vaas-lesson", lesson.id])
      await queryClient.invalidateQueries(["lesson", lesson.id])
      toast.success("Caption deleted")
      setDeleteCaptionId(null)
    } catch (err) {
      openV2ErrorToast(err, "There was an error deleting the srt file")
    }
  }

  const captionLabel = useMemo(
    () => (caption: VaasCaption) =>
      caption.srt_language
        ? SRT_LANGUAGES[caption.srt_language as keyof typeof SRT_LANGUAGES] ||
          caption.srt_language
        : "",
    [],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Media and captions</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="rounded-md border border-border bg-muted/20 p-4">
          <div className="overflow-hidden rounded-md">
            {videoQuery.isLoading ? (
              <div className="flex min-h-[220px] items-center justify-center">
                <LuLoaderCircle className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : videoQuery.data?.data?.readyToStream ? (
              <VaasVideoPlayer
                poster={lessonThumbnail}
                url={`https://customer-${import.meta.env.VITE_CLOUDFLARE_CUSTOMER_ID}.cloudflarestream.com/${videoQuery.data.data.videoToken}/manifest/video.m3u8`}
              />
            ) : (
              <div className="flex min-h-[220px] items-center justify-center rounded-md border border-dashed border-border bg-background px-4 text-sm text-muted-foreground">
                {lesson?.video_id
                  ? "Video not ready to stream yet. Come back in a few minutes."
                  : "No video attached yet."}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {isPublished ? (
            <div className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
              VAAS courses that have been published cannot upload or replace
              media.
            </div>
          ) : null}

          <div className="rounded-md border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
            <div className="font-medium text-foreground">Video file</div>
            <div className="mt-2">
              {videoQuery.data?.data?.fileName ||
                mediaKeyName ||
                "No uploaded video"}
            </div>
          </div>

          {showVideoLoading ? (
            <div className="flex min-h-[120px] flex-col items-center justify-center rounded-md border border-border bg-muted/50 px-4 text-center">
              <LuLoaderCircle className="mb-3 h-6 w-6 animate-spin text-primary" />
              <div className="text-sm font-medium text-foreground">
                {videoProgressMessage || "Uploading video"}
              </div>
              {uploadProgress > 0 ? (
                <div className="mt-2 text-sm text-muted-foreground">
                  {uploadProgress}% uploaded
                </div>
              ) : null}
            </div>
          ) : (
            <Dropzone
              accept={{ "video/*": [] }}
              disabled={isPublished}
              loading={false}
              onDrop={onVideoDrop}
              onError={onVideoError}
              title="Drop lesson video here"
            >
              <Button disabled={isPublished} variant="outline">
                {videoQuery.data?.data?.readyToStream
                  ? "Reupload / change video"
                  : "Upload video"}
              </Button>
            </Dropzone>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="text-sm font-medium text-foreground">Captions</div>
          <div className="flex flex-wrap gap-4">
            {(lesson?.captions || []).map((caption) => (
              <div
                className="flex min-w-[220px] flex-col gap-3 rounded-md border border-border bg-muted/20 p-4"
                key={caption.id}
              >
                <Dropzone
                  accept={{
                    "application/x-subrip": [".srt"],
                    "text/plain": [".srt"],
                  }}
                  disabled={isPublished}
                  loading={srtLoading}
                  onDrop={(acceptedFiles) =>
                    onSrtDrop(acceptedFiles, true, caption)
                  }
                  onError={onSrtError}
                  title="Replace .srt"
                  className="w-full"
                >
                  <div className="relative flex min-h-[96px] w-full items-center justify-center rounded-md border border-dashed border-border bg-background">
                    {(lesson?.captions?.length || 0) > 1 && (
                      <button
                        className="absolute right-2 top-2 text-destructive"
                        disabled={isPublished}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          if (!isPublished) {
                            setDeleteCaptionId(caption.id)
                          }
                        }}
                        type="button"
                      >
                        <LuTrash2 className="h-4 w-4" />
                      </button>
                    )}
                    <LuFileText className="h-8 w-8 text-primary" />
                  </div>
                </Dropzone>
                <div className="text-sm text-foreground">
                  {caption?.srt_key?.split("-")?.[1] || caption.srt_key}
                </div>
                <div className="text-sm text-muted-foreground">
                  {captionLabel(caption)}
                </div>
                <button
                  className="text-left text-sm text-primary underline-offset-4 hover:underline"
                  onClick={() => handleOpenSrt(caption.id)}
                  type="button"
                >
                  View srt file
                </button>
              </div>
            ))}

            <div className="min-w-[220px]">
              <Dropzone
                accept={{
                  "application/x-subrip": [".srt"],
                  "text/plain": [".srt"],
                }}
                disabled={!lesson?.video_id || isPublished}
                onDrop={onSrtDrop}
                loading={srtLoading}
                onError={onSrtError}
                title="Add .srt file here"
              />
            </div>
          </div>
        </div>
      </CardContent>
      <VaasSrtLanguageDialog
        caption={pendingSrtCaption}
        captions={lesson?.captions || []}
        onConfirm={async (language) => {
          await uploadSrtFile(
            pendingSrtFiles[0],
            pendingSrtCaption?.id || null,
            language,
          )
          setPendingSrtCaption(null)
          setPendingSrtFiles([])
          setSrtLanguageDialogOpen(false)
        }}
        onOpenChange={(open) => {
          setSrtLanguageDialogOpen(open)
          if (!open) {
            setPendingSrtCaption(null)
            setPendingSrtFiles([])
          }
        }}
        open={srtLanguageDialogOpen}
      />
      <VaasConfirmDialog
        confirmLabel="Delete"
        description="This will remove the selected caption from this VAAS lesson."
        onConfirm={() => handleDeleteSrt(deleteCaptionId)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteCaptionId(null)
          }
        }}
        open={Boolean(deleteCaptionId)}
        title="Delete Caption"
      />
    </Card>
  )
}

export default VaasMediaSection
